// src/components/AssessmentQuiz.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import translations from './translations';
import './AssessmentQuiz.css';

// 引入不同語言版本的 quizData
import { quizData as quizDataTW } from '../data/quizData-tw';
import { quizData as quizDataCN } from '../data/quizData-cn';
import { quizData as quizDataVN } from '../data/quizData-vn';
import { quizData as quizDataEN } from '../data/quizData-en';
import { initStartTime, computeAndSaveTimeTaken } from '../utils/timing';


// 工具函數：打亂題目順序
const shuffleArray = (array) => array.sort(() => Math.random() - 0.5);

function AssessmentQuiz() {
  const navigate = useNavigate();
  const location = useLocation();

  // 從 URL 參數獲取語言，默認為 zh-CN
  const searchParams = new URLSearchParams(location.search);
  const defaultLang = searchParams.get('defaultLang') || 'zh-CN';
  const resume = searchParams.get('resume') === '1';
  const restart = searchParams.get('restart') === '1';
  const t = translations[defaultLang] || translations['zh-CN'];

  // 根據語言選擇對應的 quizData
  let quizData;
  switch (defaultLang) {
    case 'zh-CN':
      quizData = quizDataCN;
      break;
    case 'vi':
      quizData = quizDataVN;
      break;
    case 'en':
      quizData = quizDataEN;
      break;
    default:
      quizData = quizDataTW;
  }

  // 使用狀態管理題目、索引、答案和提交狀態
  const [shuffledQuizData, setShuffledQuizData] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 初始化 quizAnswers，防止 localStorage 解析錯誤
  const [answers, setAnswers] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('quizAnswers')) || {};
    } catch (e) {
      return {};
    }
  });



  useEffect(() => {
    // ✅ 使用工具自動處理時間格式正確性
    initStartTime();

    if (quizData && quizData.length > 0) {
      const all = quizData.map((q, i) => ({ ...q, id: i + 1 }));
      const byId = new Map(all.map(q => [q.id, q]));
      const key = `quizOrder_${defaultLang}`;
      let order;
      try { order = JSON.parse(localStorage.getItem(key) || '[]'); } catch { order = []; }
      const valid = new Set(['0','1','2']);
      const polluted = Object.values(answers || {}).some(v => !valid.has(String(v)));
      if (polluted) {
        localStorage.removeItem('quizAnswers');
        localStorage.removeItem(key);
        order = all.map(q => q.id).sort(() => Math.random() - 0.5);
        localStorage.setItem(key, JSON.stringify(order));
        setShuffledQuizData(order.map(id => byId.get(id)));
        setCurrentIndex(0);
        return;
      }
      if (!Array.isArray(order) || order.length !== all.length) {
        order = all.map(q => q.id).sort(() => Math.random() - 0.5);
        localStorage.setItem(key, JSON.stringify(order));
      }
      setShuffledQuizData(order.map(id => byId.get(id)));
      const firstUnansweredIndex = order.findIndex(id => !valid.has(String((answers || {})[id])));
      setCurrentIndex(firstUnansweredIndex >= 0 ? firstUnansweredIndex : 0);
    }
  }, [defaultLang]); // 監聽語言變化，確保 quizData 變更時重新載入



  // 確保題目正確加載
  if (!shuffledQuizData.length) {
    return <div>📥 加載測評題目中...</div>;
  }

  // 當前題目
  const currentQuestion = shuffledQuizData[currentIndex] || {};
  if (!currentQuestion.id) {
    return <div>⚠️ 加載題目時發生錯誤，請重新整理</div>;
  }

  // 計算進度百分比
  const progress = Math.round(((currentIndex + 1) / shuffledQuizData.length) * 100);

  // 處理選擇答案的邏輯
  const handleAnswer = (answer) => {
    if (!currentQuestion.id) return;
    const updatedAnswers = { ...answers, [currentQuestion.id]: answer || '未知' };
    setAnswers(updatedAnswers);
    localStorage.setItem('quizAnswers', JSON.stringify(updatedAnswers));

    // 如果還有下一題，則跳到下一題
    if (currentIndex < shuffledQuizData.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  // 返回上一題
  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  // 提交測評並跳轉
  const handleSubmit = () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    const valid = new Set(['0','1','2']);
    const missingIndex = shuffledQuizData.findIndex(q => !valid.has(String((answers || {})[q.id])));

    if (missingIndex >= 0) {
      alert(t.incompleteAlert || '您还有未完成的题目，请先完成再提交。');
      setCurrentIndex(missingIndex);
      setIsSubmitting(false);
      return;
    }
    computeAndSaveTimeTaken();
    setTimeout(() => {
      navigate(`/submit?defaultLang=${defaultLang}`);
    }, 500);
  };

  // 根據答案設定選中的按鈕樣式
  const getButtonClass = (option) => (answers[currentQuestion.id] === option ? 'btn-selected' : 'btn-default');

  return (
    <div className="assessment-quiz">
      {/* 進度條 */}
      <div className="progress-bar-container">
        <div className="progress-bar" style={{ width: `${progress}%` }}></div>
      </div>

      {/* 題目 */}
      <div className="question-card">
        <h2>
          {t.quizTitle}: {t.questionPrefix} {currentIndex + 1} {t.ofTotal} {shuffledQuizData.length}
        </h2>
        <p className="question-text">{currentQuestion.question || t.noQuestion}</p>

        {/* 選項按鈕 */}
        <div className="options">
          <button className={getButtonClass('2')} onClick={() => handleAnswer('2')}>
            {t.optionYes2}
          </button>
          <button className={getButtonClass('1')} onClick={() => handleAnswer('1')}>
            {t.optionMaybe2}
          </button>
          <button className={getButtonClass('0')} onClick={() => handleAnswer('0')}>
            {t.optionNo2}
          </button>
        </div>
      </div>

      {/* 上一題按鈕 */}
      {currentIndex > 0 && (
        <button className="previous-button" onClick={handlePrevious}>
          {t.previousButton}
        </button>
      )}

      {/* 完成提交按鈕 */}
      {currentIndex === shuffledQuizData.length - 1 && (
        <button className="submit-button" onClick={handleSubmit} disabled={isSubmitting}>
          {t.submitButton}
        </button>
      )}
    </div>
  );
}

export default AssessmentQuiz;
